import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';

/**
 * Styling for the Account Overview read-mode field primitives (`OverviewField`) and the
 * flat field `grid` the cards lay them out in. All tokens come from `THEME` apart from the 15px
 * value text, an exact hifi-design requirement with no scale equivalent.
 */

/**
 * Shared label typography for every read/edit field in the Account Overview cards. Sentence case at
 * regular weight on purpose: an all-caps 600-weight label carries nearly the optical weight of the
 * larger value beside it, which flattens the label/value hierarchy Success scans a card by.
 */
export const overviewFieldLabelTypography = {
	color: THEME.colors.text.light,
	fontSize: THEME.typography.fontSizes.xs,
	fontWeight: THEME.typography.fontWeights.standard,
};

/**
 * Shared value typography for the Account Overview fields. 15px has no equivalent on the
 * `THEME.typography` scale (which steps 14 -> 16) but is an exact hifi-design requirement, so the
 * fields that render their own value node take it from here rather than restating the literal.
 */
export const overviewFieldValueTypography = {
	color: THEME.colors.text.main,
	fontSize: 15,
	lineHeight: 1.3,
};

export const overviewFieldStyles = StyleSheet.create({
	field: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	spanTwo: {
		gridColumn: 'span 2',
	},
	label: {
		...overviewFieldLabelTypography,
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.xs,
	},
	// Info affordance next to a field label, opening the field's hover card.
	infoIcon: {
		alignItems: 'center',
		cursor: 'pointer',
		display: 'inline-flex',
	},
	infoContent: {
		maxWidth: 320,
		padding: THEME.spacing.md,
	},
	value: {
		...overviewFieldValueTypography,
	},
	// Values stay the same almost-black as normal values (legacy parity) — dimming empty/Unknown to
	// `text.light` made them gray-on-gray against the (also light-gray) label and hard to read.
	valueMuted: {
		color: THEME.colors.text.main,
	},
	// The negative margin cancels the padding and border so the label and value keep the column's
	// left edge, letting the callout bleed into the grid gap instead of insetting its own text.
	fieldDanger: {
		backgroundColor: THEME.colors.danger.light,
		border: `1px solid ${THEME.colors.danger.main}`,
		borderRadius: THEME.common.borderRadius,
		margin: -(THEME.spacing.sm + 1),
		padding: THEME.spacing.sm,
	},
	// `danger.dark`, not `danger.main`: only the dark red clears 4.5:1 on the tint, so hierarchy
	// comes from weight rather than color.
	labelDanger: {
		color: THEME.colors.danger.dark,
	},
	valueDanger: {
		color: THEME.colors.danger.dark,
		fontWeight: THEME.typography.fontWeights.bold,
	},
	subLine: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
	},
	subLinePendingCancellation: {
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	// The gray would sit under 2.5:1 on the tint, so inside a callout the sub-line takes its red.
	subLineOnDanger: {
		color: 'inherit',
		fontWeight: THEME.typography.fontWeights.standard,
	},
	// Fixed four columns rather than auto-fit: the field order is arranged so the pairs Success reads
	// together (Creation over Renewal Date, Last over Next Meeting) stack in one column, and a track
	// count that grows with the viewport re-flows every field into a different column on a wide screen.
	// `minmax(0, 1fr)` keeps a long value from widening its track past an equal share.
	grid: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		gap: THEME.spacing.lg,
		[THEME.queries.lg]: {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		},
		[THEME.queries.sm]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
});
